import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { Grid } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons'
import useStyles from './SecretSoundtrackFormula.styles'
import { translate } from '../../../../utilities/language'
import { getAttribution } from '../../../../utilities/content'
import {
  ARTICLE_IMAGE_AVIF,
  ARTICLE_IMAGE_JPEG,
  SECRET_SOUNDTRACK_FORMULA_TITLE_HTML,
  SECRET_SOUNDTRACK_FORMULA_TITLE_HTML_ES,
  SECRET_SOUNDTRACK_FORMULA_TITLE_HTML_FR,
  SECRET_SOUNDTRACK_FORMULA_TITLE_HTML_KO,
  SECRET_SOUNDTRACK_FORMULA_ARTICLE_PUBLISHED_STR,
} from '../../../../static-data/secret-soundtrack-formula-for-gyms-and-cafes-data'

const BackToArticle: React.ComponentType<any> = dynamic(
  () => import('../../../../components/controls/BackToArticle'),
  {
    ssr: true,
  }
)
const ReadyToTransform: React.ComponentType<any> = dynamic(
  () => import('../../../../components/controls/ReadyToTransform'),
  {
    ssr: true,
  }
)

export default function SecretSoundtrackFormula(props: any) {
  const { classes } = useStyles()
  const selectedLanguage: any = props?.selectedLanguage
  const avifCompatible = props?.avifCompatible

  const articleImage = useMemo(() => {
    return (
      <Image
        width={512}
        height={279}
        sizes="(max-width: 350px) 256px, 
               (max-width: 430px) 300px, 
               (max-width: 590px) 384px, 
               512px"
        src={avifCompatible ? ARTICLE_IMAGE_AVIF : ARTICLE_IMAGE_JPEG}
        alt=""
        priority={true}
        style={{
          marginTop: '30px',
          borderRadius: '10px',
          display: 'block',
          width: '100%',
          maxWidth: '512px',
          height: 'auto',
        }}
      />
    )
  }, [avifCompatible])

  return (
    <div className={classes.mainGrid}>
      <div className={classes.innerDiv}>
        <Grid columns={24}>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <h1 className={classes.standardText + ' ' + classes.h1Tag}>
                {translate(
                  SECRET_SOUNDTRACK_FORMULA_TITLE_HTML,
                  SECRET_SOUNDTRACK_FORMULA_TITLE_HTML_ES,
                  SECRET_SOUNDTRACK_FORMULA_TITLE_HTML_FR,
                  SECRET_SOUNDTRACK_FORMULA_TITLE_HTML_KO,
                  selectedLanguage
                )}
              </h1>
              {getAttribution(
                translate(
                  'Editor',
                  'Editora',
                  'Éditrice',
                  '편집자',
                  selectedLanguage
                ),
                SECRET_SOUNDTRACK_FORMULA_ARTICLE_PUBLISHED_STR,
                selectedLanguage,
                false
              )}
              {articleImage}
            </div>
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Walk into a cozy coffee shop at 8 AM, and you’ll hear
                  well-chosen indie music in the background, creating a
                  productive morning atmosphere. Step into a crowded fitness
                  studio during evening rush hour, and high-energy beats are
                  playing through premium speakers. These companies appear to be
                  a universe apart, one selling lattes and pastry, the other
                  selling sweat and stamina. Yet they share one powerful secret:
                  both have mastered the art of strategic sound.
                </>,
                <>
                  Entra en una acogedora cafetería a las 8 a.m. y escuchará
                  música independiente bien elegida de fondo, creando una
                  atmósfera matutina productiva. Entra en un gimnasio lleno de
                  gente durante la hora pico de la tarde y escuchas ritmos
                  llenos de energía. Reproducción a través de altavoces premium.
                  Estas empresas parecen ser un universo aparte: una vende café
                  con leche y pasteles, la otra vende sudor y resistencia. Sin
                  embargo, comparten un poderoso secreto: ambos dominan el arte
                  del sonido estratégico.
                </>,
                <>
                  Entrez dans un café confortable à 8 heures du matin et vous
                  entendrez musique indie bien choisie en arrière-plan, créant
                  un ambiance matinale productive. Entrez dans une salle de
                  sport bondée studio aux heures de pointe du soir, et des
                  rythmes énergiques sont jouer via des haut-parleurs haut de
                  gamme. Ces sociétés semblent être un univers à part, l'un
                  vendant des cafés au lait et des pâtisseries, l'autre vendre
                  de la sueur et de l'endurance. Pourtant, ils partagent un
                  secret puissant&nbsp;: tous deux maîtrisent l’art du son
                  stratégique.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    오전
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    8시에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    아늑한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    커피숍에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    들어서면
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    배경음악으로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    엄선된
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    인디
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    음악이
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    들려오며
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    생산적인
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    아침
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    분위기를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    조성합니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    저녁
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    출퇴근
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    시간에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    붐비는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    피트니스
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    스튜디오에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    들어서면
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    프리미엄
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    스피커를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    통해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    에너지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    넘치는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    비트가
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    흘러나옵니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    이들
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    회사는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    서로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    다른
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    우주처럼
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    보입니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하나는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    라떼와
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    패스트리를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    판매하고
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    다른
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하나는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    땀과
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    체력을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    판매합니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그러나
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그들은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    하나의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    강력한
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    비밀을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    공유합니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    둘
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    다
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    전략적인
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    사운드의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    기술을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    숙달했다는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    것입니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  The most winning gyms and cafes do not entrust their{' '}
                  <span className={classes.spanEmphasis}>
                    background music strategy
                  </span>{' '}
                  to the fate of the draw. They have found a formula that can
                  convert sound from background noise into a driving engine of
                  revenue that shapes customer behavior, extends visits, and
                  builds unbreakable brand loyalty.
                </>,
                <>
                  Los gimnasios y cafeterías más ganadores no confían su{' '}
                  <span className={classes.spanEmphasis}>
                    estrategia de música ambiental
                  </span>{' '}
                  al destino del sorteo. Han encontrado una fórmula que puede
                  convertir el sonido del ruido de fondo en un motor de ingresos
                  que moldea el comportamiento del cliente, extiende las visitas
                  y genera una lealtad inquebrantable a la marca.
                </>,
                <>
                  Les salles de sport et les cafés les plus gagnants ne confient
                  pas leur{' '}
                  <span className={classes.spanEmphasis}>
                    stratégie de musique de fond
                  </span>{' '}
                  au sort du tirage au sort. Ils ont trouvé une formule qui peut
                  convertir le son du bruit de fond en un moteur de revenus qui
                  façonne le comportement des clients, prolonge les visites et
                  renforce une fidélité inébranlable à la marque.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    가장
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    많은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    승리를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    거둔
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    체육관과
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    카페는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    배경음악
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    전략을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    추첨의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    운명에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    맡기지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    않습니다.
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    그들은
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    배경
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소음의
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소리를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    고객
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    행동을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    형성하고,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    방문을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    늘리고,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    깨지지
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    않는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    브랜드
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    충성도를
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    구축하는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    수익
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    창출
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    엔진으로
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    전환할
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    수
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    공식을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    찾았습니다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <p className={classes.pParagraph}>
              {translate(
                <>
                  Let’s decode the formula that’s hiding in plain sight&nbsp;—
                  or rather, in plain sound.
                </>,
                <>
                  Descifremos la fórmula que se esconde a plena vista, o más
                  bien, en claro sonido.
                </>,
                <>
                  Décodeons la formule qui se cache à la vue de tous, ou plutôt
                  au son.
                </>,
                <>
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    눈에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    잘
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    띄는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    곳에,
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    아니
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    오히려
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    눈에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    보이는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    소리
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    속에
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    숨어
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    있는
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    공식을
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    해독해
                  </span>{' '}
                  <span aria-hidden={true} className={classes.asianTextBlock}>
                    봅시다.
                  </span>
                </>,
                selectedLanguage
              )}
            </p>
            <section id="science-of-business-soundtracks">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Science Behind the Soundtrack Formula</>,
                  <>La ciencia detrás de la fórmula de la banda sonora</>,
                  <>La science derrière la formule de la bande sonore</>,
                  <>
                    <span
                      aria-hidden={true}
                      className={classes.asianTextBlock}
                    ></span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    It is important to understand the psychology first before
                    getting down to the formula. Music does not simply fill
                    silence, but it actually determines how your customers
                    experience your space and the duration of their stay.
                  </>,
                  <>
                    Es importante comprender primero la psicología antes de
                    pasar a la fórmula. La música no simplemente llena el
                    silencio, sino que también determina cómo sus clientes
                    experimentan su espacio y la duración de su estadía.
                  </>,
                  <>
                    Il est important de comprendre la psychologie avant de
                    passer à la formule. La musique ne remplit pas simplement le
                    silence, mais elle détermine réellement la façon dont vos
                    clients perçoivent votre espace et la durée de leur séjour.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공식에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      들어가기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      먼저
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리학을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이해하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중요합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단순히
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      침묵을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      채우는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아니라
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고객이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공간을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      경험하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방식과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체류
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기간을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      결정합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanEmphasis}>In cafes:</span>
                    &nbsp;Music creates psychological comfort that encourages
                    lingering.{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10497766/"
                      title="The effects of background music tempo on consumer variety-seeking behavior: the mediating role of arousal"
                      rel="noopener noreferrer"
                    >
                      <span className={classes.spanPre}>
                        Research&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    shows that medium-paced songs (70–90 BPM) raise the average
                    expenditure of customers by making them calm and open to
                    additional purchases.
                  </>,
                  <>
                    <span className={classes.spanEmphasis}>En cafeterías:</span>
                    &nbsp;La música crea consuelo psicológico que fomenta la
                    perseverancia.&nbsp;
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10497766/"
                      title="Los efectos del tempo de la música de fondo en el comportamiento de búsqueda de variedad del consumidor: el papel mediador de la excitación"
                      rel="noopener noreferrer"
                    >
                      Las investigaciones{' '}
                      <span className={classes.spanPre}>
                        muestran&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    que las canciones de ritmo medio (70 a 90 BPM) aumentan el
                    gasto promedio de los clientes al calmarlos y abrirlos a
                    compras adicionales.
                  </>,
                  <>
                    <span className={classes.spanEmphasis}>In cafes:</span>
                    &nbsp;La musique crée un confort psychologique qui encourage
                    à s'attarder.{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10497766/"
                      title="The effects of background music tempo on consumer variety-seeking behavior: the mediating role of arousal"
                      rel="noopener noreferrer"
                    >
                      Les{' '}
                      <span className={classes.spanPre}>
                        recherches&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    montrent que les chansons au rythme moyen (70 à 90 BPM)
                    augmentent les dépenses moyennes des clients en les rendant
                    calmes et ouverts à des achats supplémentaires.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      오래
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      머물도록
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      장려하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      편안함을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만들어줍니다.
                    </span>{' '}
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10497766/"
                      title="배경음악 템포가 소비자의 다양성 추구행동에 미치는 영향: 각성의 매개역할"
                      rel="noopener noreferrer"
                    >
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        연구에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        따르면&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중간
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      속도의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      노래(70~90BPM)는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고객을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      차분하게
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만들고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      추가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구매에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개방적으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      만들어
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고객의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      평균
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      지출을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      높이는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나타났습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    In gyms, music turns into a performance enhancer. Research
                    published in&nbsp;
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href="https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2023.1293783/full"
                      title="The effects of pre-task music on exercise performance and associated psycho-physiological responses: a systematic review with multilevel meta-analysis of controlled studies"
                      rel="noopener noreferrer"
                    >
                      Frontiers in{' '}
                      <span className={classes.spanPre}>
                        Psychology&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    found that motivational music can enhance workout intensity
                    by up to 15% while reducing perceived exertion.
                  </>,
                  <>
                    En los gimnasios, la música se convierte en un potenciador
                    del rendimiento. Una investigación publicada en&nbsp;
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href="https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2023.1293783/full"
                      title="Los efectos de la música previa a la tarea sobre el rendimiento del ejercicio y las respuestas psicofisiológicas asociadas: una revisión sistemática con metanálisis multinivel de estudios controlados"
                      rel="noopener noreferrer"
                    >
                      "Frontiers in{' '}
                      <span className={classes.spanPre}>
                        Psychology"&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    encontró que la música motivadora puede mejorar la
                    intensidad del entrenamiento hasta en un 15% y al mismo
                    tiempo reducir el esfuerzo percibido.
                  </>,
                  <>
                    Dans les salles de sport, la musique devient un levier de
                    performance. Recherche publié dans&nbsp;
                    <a
                      className={classes.aHref}
                      target="_blank"
                      href="https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2023.1293783/full"
                      title="Les effets de la musique avant une tâche sur la performance physique et les réponses psychophysiologiques associées : une revue systématique avec méta-analyse à plusieurs niveaux d'études contrôlées"
                      rel="noopener noreferrer"
                    >
                      "Frontiers in{' '}
                      <span className={classes.spanPre}>
                        Psychology"&nbsp;
                        <IconExternalLink />
                      </span>
                    </a>{' '}
                    a révélé que la musique de motivation peut améliorer
                    l'intensité de l'entraînement jusqu'à 15&nbsp;% tout en
                    réduisant l'effort perçu.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관에서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성능
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      변합니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      <a
                        className={classes.aHref}
                        target="_blank"
                        href="https://www.frontiersin.org/journals/psychology/articles/10.3389/fpsyg.2023.1293783/full"
                        title="Les effets de la musique avant une tâche sur la performance physique et les réponses psychophysiologiques associées : une revue systématique avec méta-analyse à plusieurs niveaux d'études contrôlées"
                        rel="noopener noreferrer"
                      >
                        "Frontiers in{' '}
                        <span className={classes.spanPre}>
                          Psychology"&nbsp;
                          <IconExternalLink />
                        </span>
                      </a>{' '}
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      발표된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      연구에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르면
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여가
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      되는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      인지된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      운동량을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      줄이면서
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      운동
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      강도를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최대
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      15%까지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상시킬
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      수
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      나타났습니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanEmphasis}>
                      Both environments
                    </span>{' '}
                    use curated playlists for business to stimulate a particular
                    psychological response:
                  </>,
                  <>
                    <span className={classes.spanEmphasis}>Ambos entornos</span>{' '}
                    utilizan listas de reproducción seleccionadas para empresas
                    para estimular una respuesta psicológica particular:
                  </>,
                  <>
                    <span className={classes.spanEmphasis}>
                      Les deux environnements
                    </span>{' '}
                    utilisent des listes de lecture organisées pour les
                    entreprises afin de stimuler une réponse psychologique
                    particulière&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      환경
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      특정
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      심리적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반응을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자극하기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      선별된
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      목록을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Tempo control:</span>{' '}
                      &nbsp;Regulates energy and pace
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Control de tempo:
                      </span>{' '}
                      &nbsp;Regula la energía y el ritmo
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Contrôle du tempo&nbsp;:
                      </span>{' '}
                      &nbsp;Régule l'énergie et le rythme
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제어:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        속도를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조절합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Consistency:</span>{' '}
                      &nbsp;Creates predictable, comfortable experiences
                    </>,
                    <>
                      <span className={classes.spanBolder}>Consistencia:</span>{' '}
                      &nbsp;Crea experiencias predecibles y cómodas
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Cohérence&nbsp;:
                      </span>{' '}
                      &nbsp;Crée des expériences prévisibles et confortables
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        일관성:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        예측
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        가능하고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        편안한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        경험을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        창출합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Volume management:
                      </span>{' '}
                      &nbsp;Influences social behavior and focus
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Gestión de volumen:
                      </span>{' '}
                      &nbsp;Influye en el comportamiento social y el enfoque
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Gestion de volumes&nbsp;:
                      </span>{' '}
                      &nbsp; Influence le comportement social et la
                      concentration
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        볼륨
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관리:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사회적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        행동과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중력에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영향을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        미칩니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Genre selection:
                      </span>{' '}
                      &nbsp;Signals brand identity and attracts target
                      demographics
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Selección de género:
                      </span>{' '}
                      &nbsp;Señala la identidad de la marca y atrae al grupo
                      demográfico objetivo
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Sélection du genre&nbsp;:
                      </span>{' '}
                      &nbsp;Signale l’identité de la marque et attire les
                      données démographiques cibles
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장르
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        선택:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        브랜드
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아이덴티티를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        알리고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        타겟
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        인구통계를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유인합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The key insight? Neither successful cafes nor gyms play what
                    they prefer&nbsp;— they play what benefits their business
                    goals.
                  </>,
                  <>
                    ¿La idea clave? Ni los cafés ni los gimnasios exitosos
                    juegan lo que prefieren: juegan lo que beneficia sus
                    objetivos comerciales.
                  </>,
                  <>
                    L’idée clé ? Ni les cafés ni les salles de sport à succès ne
                    jouent ce qu'ils préfèrent&nbsp;: ils jouent ce qui profite
                    à leurs objectifs commerciaux.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      핵심
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      통찰력은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      무엇입니까?
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성공적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페나
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      자신이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      선호하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      하지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      않습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      목표에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      도움이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      되는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      일을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="four-principles-of-good-business-music">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Common Formula: 4 Shared Principles</>,
                  <>La fórmula común: 4 principios compartidos</>,
                  <>La formule commune&nbsp;: 4 principes partagés</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공통
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공식:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      4가지
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공유
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      원칙
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Despite having different purposes, both industries follow
                    the same effective branding principles for business:
                  </>,
                  <>
                    A pesar de tener propósitos diferentes, ambas industrias
                    siguen los mismos principios de marca eficaces para las
                    empresas:
                  </>,
                  <>
                    Malgré leurs objectifs différents, les deux secteurs suivent
                    les mêmes principes de marque efficaces pour les
                    entreprises&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      서로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다른
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      목적에도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불구하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      산업
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동일한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      효과적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      브랜딩
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      원칙을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따릅니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="principle-1-energy-alignment">
                <h3 className={classes.subSection}>
                  {translate(
                    <>1. Energy Alignment</>,
                    <>1. Alineación energética</>,
                    <>1. Alignement énergétique</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        1.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정렬
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Set the music to the mood you want your customers to feel:
                    </>,
                    <>
                      Configure la música según el estado de ánimo que desea que
                      sientan sus clientes:
                    </>,
                    <>
                      Réglez la musique selon l'ambiance que vous souhaitez que
                      vos clients ressentent&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        느끼기를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        원하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분위기에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        맞춰
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        설정하세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Cafes:</span>{' '}
                        &nbsp;Calm focus or comfortable socializing
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés:</span>{' '}
                        &nbsp;Concentración tranquila o socialización cómoda
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés&nbsp;:</span>{' '}
                        &nbsp;Concentration calme ou socialisation confortable
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          차분한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          집중
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          또는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          편안한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사교
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          활동
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Gyms:</span>{' '}
                        &nbsp;Peak physical performance and motivation
                      </>,
                      <>
                        <span className={classes.spanBolder}>Gimnasios:</span>{' '}
                        &nbsp;Máximo rendimiento físico y motivación
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Salles de sport&nbsp;:
                        </span>{' '}
                        &nbsp;Performance physique et motivation
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체육관:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          최고의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          신체
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          활동과
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          동기
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          부여
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Both succeed by understanding their customers’ desired
                      emotional state, and they bring in sound to help them
                      switch to that emotional state.
                    </>,
                    <>
                      Ambos tienen éxito al comprender los deseos de sus
                      clientes, estado emocional, y traen sonido para ayudarles
                      a cambiar a ese estado emocional.
                    </>,
                    <>
                      Tous deux réussissent en comprenant l’état émotionnel
                      souhaité par leurs clients, et ils apportent du son pour
                      les aider à passer à cet état émotionnel.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        둘
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        원하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상태를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이해함으로써
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        성공하고,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        해당
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감정
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상태로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전환할
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있도록
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소리를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        제공합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="principle-2-time-management">
                <h3 className={classes.subSection}>
                  {translate(
                    <>2. Time Management Through Sound</>,
                    <>2. Gestión del tiempo a través del sonido</>,
                    <>2. Gestion du temps grâce au son</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        2.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소리를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        통한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시간
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관리
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      The tactical management of tempo and intensity to manage
                      customer flow:
                    </>,
                    <>
                      La gestión táctica del ritmo y la intensidad para
                      gestionar el flujo de clientes:
                    </>,
                    <>
                      La gestion tactique du tempo et de l’intensité pour gérer
                      le flux client&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        흐름을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관리하기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        강도의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전술적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        관리:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Morning cafes:
                        </span>{' '}
                        &nbsp;Lively, yet not aggressive&nbsp;— stimulating
                        productivity.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Cafés de la mañana:
                        </span>{' '}
                        &nbsp;Animada, pero no agresiva, estimula la
                        productividad.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Matinées dans les cafés&nbsp;:
                        </span>{' '}
                        Vif, mais pas agressif. Stimuler la productivité.{' '}
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          아침의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          활기차지만
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          공격적이지는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          않습니다.
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          생산성을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          자극합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Lunch rush cafes:
                        </span>{' '}
                        &nbsp;A little bit faster tempo to maintain turnover.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Cafés para el almuerzo:
                        </span>{' '}
                        &nbsp;Un ritmo un poco más rápido para mantener la
                        rotación.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Déjeuner dans les cafés&nbsp;:
                        </span>{' '}
                        Un rythme un peu plus rapide pour maintenir le turnover.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          점심시간의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          :
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          회전율
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          유지를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          위해
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          조금
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          더
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          빠른
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Afternoon cafes:
                        </span>{' '}
                        &nbsp;Relaxing tracks that encourage lingering (and
                        additional purchases).
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Cafés de la tarde:
                        </span>{' '}
                        &nbsp;Relaxing tracks that encourage lingering (and
                        additional purchases).
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Après-midi dans les cafés&nbsp;:
                        </span>
                        &nbsp;Des morceaux relaxants qui encouragent à
                        s'attarder (et à faire des achats supplémentaires).
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          오후
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          오래
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          머물거나
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          추가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          구매를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          유도하는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          편안한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          트랙입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Similarly, gyms use the following techniques in creating
                      energy with music:
                    </>,
                    <>
                      De manera similar, los gimnasios utilizan las siguientes
                      técnicas para crear energía con música:
                    </>,
                    <>
                      De même, les gymnases utilisent les techniques suivantes
                      pour créer de l’énergie avec de la musique&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        마찬가지로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        체육관에서는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악으로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        생성하기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        기술을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사용합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Warm-up zones:
                        </span>{' '}
                        &nbsp;100–120 BPM to gradually elevate heart rate.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Preparación del ejercicio:
                        </span>{' '}
                        &nbsp;100 a 120 BPM para elevar gradualmente la
                        frecuencia cardíaca.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Zones de préparation&nbsp;:
                        </span>{' '}
                        &nbsp;100 à 120 BPM pour augmenter progressivement la
                        fréquence cardiaque.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          준비:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고객의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심박수(분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          100~120비트)에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          맞게
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          점차적으로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          높입니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Peak workout:
                        </span>{' '}
                        &nbsp;130–150 BPM for maximum intensity.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Ejercicio máximo:
                        </span>{' '}
                        &nbsp;130–150 BPM para máxima intensidad.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Exercice maximal&nbsp;:
                        </span>{' '}
                        &nbsp;130-150 BPM pour une intensité maximale.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          최대
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          운동:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          최대
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          강도
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          동안
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          130-150회.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Cool-down areas:
                        </span>{' '}
                        &nbsp;80–100 BPM for recovery.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Zonas de relajación:
                        </span>{' '}
                        &nbsp;80–100 BPM para recuperación.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Espaces détente&nbsp;:
                        </span>{' '}
                        &nbsp;80 à 100 BPM pour la récupération.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          휴식
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          공간:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          회복을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          위한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          80-100회.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="principle-3-brand-consistency">
                <h3 className={classes.subSection}>
                  {translate(
                    <>3. Brand Identity Consistency</>,
                    <>3. Coherencia de la identidad de marca</>,
                    <>3. Cohérence de l'identité de marque</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        브랜드
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아이덴티티
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        일관성
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Your playlist is your audio logo. Successful businesses
                      have up-to-date{' '}
                      <span className={classes.spanBolder}>
                        business playlists
                      </span>{' '}
                      that:
                    </>,
                    <>
                      Tu lista de reproducción es tu logo de audio. Las empresas
                      exitosas tienen información actualizada{' '}
                      <span className={classes.spanBolder}>
                        listas de reproducción comerciales
                      </span>{' '}
                      que:
                    </>,
                    <>
                      Votre playlist est votre logo audio. Les entreprises qui
                      réussissent disposent{' '}
                      <span className={classes.spanBolder}>
                        de listes de lecture commerciales
                      </span>{' '}
                      jour qui&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재생
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목록은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        오디오
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        로고입니다.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        성공적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비즈니스에는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        같은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        최신
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비즈니스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재생목록이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Reflect brand personality</>,
                      <>Refleja la personalidad de la marca</>,
                      <>Refléter la personnalité de la marque</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          브랜드
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          개성
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          반영
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Differentiate from competitors</>,
                      <>Diferenciarse de los competidores</>,
                      <>Se différencier des concurrents</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          경쟁사와
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          차별화
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Appeal to target demographics</>,
                      <>Apelar a la demografía objetivo</>,
                      <>Appel à cibler des données démographiques</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          타겟
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인구통계에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          대한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          호소
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Remain recognizable across visits</>,
                      <>Permanezca reconocible en todas las visitas</>,
                      <>Restez reconnaissable lors des visites</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          방문
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          전반에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          걸쳐
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인식
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          가능한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          상태로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          유지
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      A trendy Brooklyn cafe doesn’t accidentally play country
                      music, and a high-end cycling studio wouldn’t play heavy
                      metal during recovery rides.
                    </>,
                    <>
                      Un café de moda en Brooklyn no toca accidentalmente música
                      country, y un estudio de ciclismo de alto nivel no tocaría
                      heavy metal durante los paseos de recuperación.
                    </>,
                    <>
                      Un café branché de Brooklyn ne joue pas de musique country
                      par hasard, et un studio de cyclisme haut de gamme ne
                      jouerait pas de heavy metal pendant ses sorties de
                      récupération.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        트렌디한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        브루클린
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        카페에서는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        실수로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        컨트리
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        틀지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않으며,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고급
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사이클링
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스튜디오에서는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        회복
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이딩
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        헤비메탈
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        틀지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
              <section id="principle-4-customer-engagement">
                <h3 className={classes.subSection}>
                  {translate(
                    <>4. Customer Engagement Through Sound</>,
                    <>4. Compromiso del cliente a través del sonido</>,
                    <>4. Engagement client grâce au son</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        4.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소리를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        통한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        참여
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Music establishes an emotional attachment that goes beyond
                      the sale:
                    </>,
                    <>
                      La música establece un vínculo emocional que va más allá
                      de la venta:
                    </>,
                    <>
                      La musique établit un attachement émotionnel qui va
                      au-delà de la vente&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        판매
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이상의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        감정적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        애착을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        형성합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Familiar-but-not-obvious tracks are comforting and not
                        distracting.
                      </>,
                      <>
                        Las pistas familiares pero no obvias son reconfortantes
                        y no distraen.
                      </>,
                      <>
                        Les morceaux familiers mais pas évidents sont
                        réconfortants et non distrayants.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          익숙하지만
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          명확하지
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          않은
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          트랙은
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          편안함을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          주며
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          방해가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          되지
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          않습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Customer engagement through sound
                        </span>{' '}
                        drives word-of-mouth (“I love the vibe here”).
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          La participación del cliente a través del sonido
                        </span>{' '}
                        impulsa el boca a boca (“Me encanta el ambiente aquí”).
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          L'engagement des clients grâce au son
                        </span>{' '}
                        stimule le bouche-à-oreille («&nbsp;J'adore l'ambiance
                        ici&nbsp;»).
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          만들어내는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          느낌에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          따라
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고객
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          참여도가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          높아집니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Consistent sonic branding creates the expectation of
                        making a repeat visit.
                      </>,
                      <>
                        La marca sonora constante crea la expectativa de repetir
                        la visita.
                      </>,
                      <>
                        Une image de marque sonore cohérente crée l’attente
                        d’une visite répétée.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          일관된
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음향
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          브랜딩은
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          재방문에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          대한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          기대를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          불러일으킵니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
            </section>
            <section id="cafe-soundtrack-strategy">
              <h2 className={classes.subSection}>
                {translate(
                  <>Cafe Soundtrack Strategy Breakdown</>,
                  <>Desglose de la estrategia de la banda sonora del café</>,
                  <>
                    Répartition de la stratégie de la bande originale du café
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사운드트랙
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분석
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The best playlists for cafes follow a dayparting strategy
                    that reflects the needs of the customers throughout the day:
                  </>,
                  <>
                    Las mejores listas de reproducción para cafeterías siguen
                    una estrategia de división del día que refleja las
                    necesidades de los clientes a lo largo del día:
                  </>,
                  <>
                    Les meilleures playlists pour les cafés suivent une
                    stratégie de répartition de la journée qui reflète les
                    besoins des clients tout au long de la journée&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      최고의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      목록은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      하루
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      종일
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      고객의
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요구를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      반영하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      시간대별
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따릅니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="cafe-soundtracks-in-mornings">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Morning (7 AM&nbsp;—&nbsp;11&nbsp;AM)
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Mañanas, de 7 a 11 horas
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Matin (de&nbsp;7h00 à&nbsp;11h00)
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        오전(오전
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        7시~오전
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        11시)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Goal:&nbsp;</span>{' '}
                      Energize without overwhelming
                    </>,
                    <>
                      <span className={classes.spanBolder}>Meta:&nbsp;</span>{' '}
                      Energizar sin abrumar
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Objective:&nbsp;
                      </span>{' '}
                      Dynamiser sans se surcharger
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        부담스럽지
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        않게
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        활력을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        불어넣으세요
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        85–100 BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        85–100 BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Tempo&nbsp;:&nbsp;
                        </span>{' '}
                        85–100 BPM
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          85-100
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Genres:&nbsp;
                        </span>{' '}
                        Indie pop, acoustic, light electronic
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Géneros:&nbsp;
                        </span>{' '}
                        Indie pop, acústico, electrónica ligera
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Genres&nbsp;:&nbsp;
                        </span>{' '}
                        Indie pop, acoustique, électronique légère
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인디
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          팝,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          어쿠스틱,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          라이트
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          일렉트로니카
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Volume:&nbsp;
                        </span>{' '}
                        Moderate (allows conversation)
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volumen:&nbsp;
                        </span>{' '}
                        Moderada (permite conversación)
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volume&nbsp;:&nbsp;
                        </span>{' '}
                        Modéré (permet la conversation)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          볼륨:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          보통(대화
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          가능)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Examples:&nbsp;
                        </span>{' '}
                        Alt-J, Bon Iver, ODESZA, Local Natives
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Ejemplos:&nbsp;
                        </span>{' '}
                        Alt-J, Bon Iver, ODESZA, nativas locales
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Exemples&nbsp;:&nbsp;
                        </span>{' '}
                        Alt-J, Bon Iver, ODESZA, Local Natives
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          예:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Alt-J,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Bon
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Iver,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          ODESZA,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Local
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Natives
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="cafe-soundtracks-in-midday">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Midday (11&nbsp;AM&nbsp;—&nbsp;3&nbsp;PM)
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Mediodía 11&nbsp;a.m.&nbsp;-&nbsp;3&nbsp;p.m.
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Midi (11h00 - 15h00)
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정오(오전
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        11시~오후
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3시)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Goal:&nbsp;</span>{' '}
                      Maintain flow, encourage turnover during lunch
                    </>,
                    <>
                      <span className={classes.spanBolder}>Meta:&nbsp;</span>{' '}
                      Mantener el flujo, fomentar la rotación durante el
                      almuerzo
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Objective&nbsp;:&nbsp;
                      </span>{' '}
                      Maintenir le flux, encourager le roulement pendant le
                      déjeuner
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분위기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        점심
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시간에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        회전율
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        촉진
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        95–110 BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        95–110 BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Tempo&nbsp;:&nbsp;
                        </span>{' '}
                        95–110 BPM
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          95-110
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Genres:&nbsp;
                        </span>{' '}
                        Upbeat indie, modern folk, chill hip-hop
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Géneros:&nbsp;
                        </span>{' '}
                        Upbeat indie, modern folk, chill hip-hop
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Genres&nbsp;:&nbsp;
                        </span>{' '}
                        Indie optimiste, folk moderne, hip-hop décontracté
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          경쾌한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인디,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          현대
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          포크
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          편안한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          힙합
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Volume:&nbsp;
                        </span>{' '}
                        Slightly elevated
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volumen:&nbsp;
                        </span>{' '}
                        Ligeramente elevada
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volume&nbsp;:&nbsp;
                        </span>{' '}
                        Légèrement surélevé
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          볼륨:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          약간
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          높음
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Examples:&nbsp;
                        </span>{' '}
                        The Lumineers, Glass Animals, Tash Sultana
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Ejemplos:&nbsp;
                        </span>{' '}
                        The Lumineers, Crystal Animals, Tash Sultana
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Exemples&nbsp;:&nbsp;
                        </span>{' '}
                        The Lumineers, Glass Animals, Tash Sultana
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          예:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          The
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Lumineers,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Crystal
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Animals,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Tash
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Sultana
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="cafe-soundtracks-in-evenigns">
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Afternoon/Evening (3&nbsp;PM&nbsp;—&nbsp;Close)
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Tarde/Noche (3&nbsp;p.m.&nbsp;-&nbsp;Fin)
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Après-midi/soirée (15h00&nbsp;-&nbsp;Fin)
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        오후/저녁(오후
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3시
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        –
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        종료)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Goal:&nbsp;</span>{' '}
                      Create a cozy atmosphere and encourage lingering.
                    </>,
                    <>
                      <span className={classes.spanBolder}>Meta:&nbsp;</span>{' '}
                      Crea un ambiente acogedor y fomenta la permanencia.
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Objective&nbsp;:&nbsp;
                      </span>
                      Créez une atmosphère chaleureuse et encouragez-vous à vous
                      attarder.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        아늑한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분위기를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        조성하고
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        오래
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        머물도록
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장려합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        70–90&nbsp;BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        70–90&nbsp;BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Tempo&nbsp;:&nbsp;
                        </span>{' '}
                        70–90&nbsp;BPM
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          70-90
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Genres:&nbsp;
                        </span>{' '}
                        Jazz, lo-fi, ambient, acoustic
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Géneros:&nbsp;
                        </span>{' '}
                        Jazz, lo-fi, ambiental, acústico
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Genres&nbsp;:&nbsp;
                        </span>{' '}
                        Jazz, lo-fi, ambiant, acoustique
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          재즈,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Lo-Fi,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          앰비언트,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          어쿠스틱
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Volume:&nbsp;
                        </span>{' '}
                        Lower, intimate
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volumen:&nbsp;
                        </span>{' '}
                        Más bajo, íntimo
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volume&nbsp;:&nbsp;
                        </span>{' '}
                        Plus bas, intime
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          볼륨:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          더
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          부드럽고
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          친밀함
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Examples:&nbsp;
                        </span>{' '}
                        Norah Jones, Chet Baker, lo-fi hip-hop playlists
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Ejemplos:&nbsp;
                        </span>{' '}
                        Norah Jones, Chet Baker, lo-fi hip-hop playlists
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Exemples&nbsp;:&nbsp;
                        </span>{' '}
                        Norah Jones, Chet Baker, lo-fi playlists hip-hop
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          예:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Norah
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Jones,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Chet
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          Baker
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Cafe playlist inspiration comes from understanding that
                      various times of the day require various moods, and your
                      music should change as the day progresses.
                    </>,
                    <>
                      La inspiración de las listas de reproducción de café
                      proviene de comprender que distintos momentos del día
                      requieren diferentes estados de ánimo y que tu música debe
                      cambiar a medida que avanza el día.
                    </>,
                    <>
                      L'inspiration des playlists de café vient de la
                      compréhension que différents moments de la journée
                      nécessitent différentes ambiances et que votre musique
                      doit changer au fil de la journée.
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        카페
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재생목록
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영감은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        하루
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다양한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시간대에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다양한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분위기가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        필요하며,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        하루가
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        진행됨에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        따라
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악도
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        달라져야
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        한다는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        점을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이해하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데서
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비롯됩니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
            </section>
            <section id="gym-soundtrack-strategy">
              <h2 className={classes.subSection}>
                {translate(
                  <>Gym Soundtrack Strategy Breakdown</>,
                  <>
                    Desglose de la estrategia de la banda sonora del gimnasio
                  </>,
                  <>
                    Répartition de la stratégie de la bande originale du gymnase
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사운드트랙
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분석
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Gym background music ideas revolve around performance
                    enhancement and motivation:
                  </>,
                  <>
                    Las ideas de música de fondo para gimnasio giran en torno a
                    la mejora del rendimiento y la motivación:
                  </>,
                  <>
                    Les idées de musique de fond pour les salles de sport
                    tournent autour de l’amélioration des performances et de la
                    motivation&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      배경
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아이디어는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성능
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      향상과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동기
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      부여를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      중심으로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이루어집니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="gym-soundtrack-in-cardio-zones">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Cardio Zones</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Zonas cardiovasculares
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>Zones cardio</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        심장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        강화
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구역
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Goal:&nbsp;</span>{' '}
                      Sustained high energy and rhythm
                    </>,
                    <>
                      <span className={classes.spanBolder}>Meta:&nbsp;</span>{' '}
                      Alta energía y ritmo sostenidos
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Objective&nbsp;:&nbsp;
                      </span>{' '}
                      Une énergie et un rythme élevés et soutenus
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        지속적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        높은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        리듬
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        130–150&nbsp;BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        130–150&nbsp;BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Tempo&nbsp;:&nbsp;
                        </span>{' '}
                        130–150&nbsp;BPM
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          130-150
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Genres:&nbsp;
                        </span>{' '}
                        EDM, dance-pop, high-energy hip-hop
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Géneros:&nbsp;
                        </span>{' '}
                        EDM, dance-pop, hip-hop de alta energía
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Genres&nbsp;:&nbsp;
                        </span>{' '}
                        EDM, dance-pop, hip-hop énergique
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          EDM,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          댄스
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          팝,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          힙합
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Volume:&nbsp;
                        </span>{' '}
                        High (but not damaging)
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volumen:&nbsp;
                        </span>{' '}
                        Alto (pero no dañino)
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volume&nbsp;:&nbsp;
                        </span>{' '}
                        Élevé (mais pas dommageable)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          볼륨:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          높음(그러나
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고통스럽지는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          않음)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Why it works:&nbsp;
                        </span>{' '}
                        Fast BPM naturally increases cadence and intensity.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Por qué funciona:&nbsp;
                        </span>{' '}
                        Los BPM rápidos aumentan naturalmente la cadencia y la
                        intensidad.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Pourquoi ça marche&nbsp;:&nbsp;
                        </span>{' '}
                        Un BPM rapide augmente naturellement la cadence et
                        l’intensité.
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          작동
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이유:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          빠른
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동으로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          케이던스와
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          강도가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          증가합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="gym-soundtrack-for-strength-training">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Strength Training Areas
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Áreas de entrenamiento de fuerza
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Zones de musculation
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        근력
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        훈련
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        영역
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Goal:&nbsp;</span>{' '}
                      Power and focus
                    </>,
                    <>
                      <span className={classes.spanBolder}>Meta:&nbsp;</span>{' '}
                      Poder y enfoque
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Objective&nbsp;:&nbsp;
                      </span>{' '}
                      Puissance et concentration
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        힘과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        120–140&nbsp;BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        120–140&nbsp;BPM
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Tempo&nbsp;:&nbsp;
                        </span>{' '}
                        120–140&nbsp;BPM
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          120-140
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Genres:&nbsp;
                        </span>{' '}
                        Rock, trap, aggressive hip-hop
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Géneros:&nbsp;
                        </span>{' '}
                        Rock, trap, hip-hop agresivo
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Genres&nbsp;:&nbsp;
                        </span>{' '}
                        Rock, trap, hip-hop agressif
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          록,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          트랩
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          음악,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          공격적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          힙합
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Volume:&nbsp;
                        </span>{' '}
                        Moderate-high
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volumen:&nbsp;
                        </span>{' '}
                        Moderado-alto
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volume&nbsp;:&nbsp;
                        </span>{' '}
                        Modéré-élevé
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          볼륨:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          보통에서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          높음
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Why it works:&nbsp;
                        </span>{' '}
                        Driving beats support explosive movements
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Por qué funciona:&nbsp;
                        </span>{' '}
                        Los ritmos de conducción admiten movimientos explosivos
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Pourquoi ça marche&nbsp;:&nbsp;
                        </span>{' '}
                        Les rythmes de conduite soutiennent les mouvements
                        explosifs
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          작동
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이유:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          드라이빙
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          비트는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          폭발적인
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          움직임을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지원합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="gym-soundtrack-for-group-fitness">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Group Fitness Classes
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Clases grupales de fitness
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Cours de fitness en groupe
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        그룹
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        피트니스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수업
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Goal:&nbsp;</span>{' '}
                      Synchronized energy and community motivation
                    </>,
                    <>
                      <span className={classes.spanBolder}>Meta:&nbsp;</span>{' '}
                      Energía sincronizada y motivación comunitaria
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Objective&nbsp;:&nbsp;
                      </span>{' '}
                      Énergie synchronisée et motivation communautaire
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동기화된
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        커뮤니티
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        동기
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        부여
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        Varies by class type (120–145 BPM average)
                      </>,
                      <>
                        <span className={classes.spanBolder}>Tempo:&nbsp;</span>{' '}
                        Varía según el tipo de clase (promedio de 120 a 145 BPM)
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Tempo&nbsp;:&nbsp;
                        </span>{' '}
                        Varie selon le type de cours (moyenne&nbsp;de
                        120&nbsp;à&nbsp;145 BPM)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          클래스
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          유형에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          따라
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          다름(평균
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          120~145)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Genres:&nbsp;
                        </span>{' '}
                        Ambient, chill electronic, instrumental
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Géneros:&nbsp;
                        </span>{' '}
                        Ambient, chill electronic, instrumental
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Genres&nbsp;:&nbsp;
                        </span>{' '}
                        Ambiante, électronique chill, instrumental
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          앰비언트,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          일렉트로닉,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인스트루멘탈
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Volume:&nbsp;
                        </span>{' '}
                        Low
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volumem:&nbsp;
                        </span>{' '}
                        Bajo
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Volume&nbsp;:&nbsp;
                        </span>{' '}
                        Douce
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          볼륨:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          소프트
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>
                          Why it works:&nbsp;
                        </span>{' '}
                        Helps transition from exertion to recovery
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Por qué funciona:&nbsp;
                        </span>{' '}
                        Ayuda a la transición del esfuerzo a la recuperación.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Pourquoi ça marche&nbsp;:&nbsp;
                        </span>{' '}
                        Aide à la transition de l'effort à la récupération
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          효능:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          운동에서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          회복으로의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          전환을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          돕습니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="gym-soundtrack-comparisons">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        The Comparison: Differences That Matter
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        La comparación: diferencias que importan
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        La comparaison&nbsp;: des différences qui comptent
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비교:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        중요한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        차이점
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Understanding the{' '}
                      <span className={classes.spanBolder}>
                        retail and fitness sound strategy
                      </span>{' '}
                      differences helps both industries streamline their
                      strategy:
                    </>,
                    <>
                      Comprender las diferencias en las{' '}
                      <span className={classes.spanBolder}>
                        estrategias sólidas de venta minorista y fitness
                      </span>{' '}
                      ayuda a ambas industrias a optimizar su estrategia:
                    </>,
                    <>
                      Comprendre les différences entre les{' '}
                      <span className={classes.spanBolder}>
                        stratégies de vente au détail et de fitness
                      </span>{' '}
                      aide les deux secteurs à rationaliser leur
                      stratégie&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        소매점
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        배경음악과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        피트니스
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        배경음악의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        차이점을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        이해하면
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보다
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중적인
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전략을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        개발하는
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        도움이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        될
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        수
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        있습니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
              </section>
            </section>
            <section id="gyms-vs-cafes-background-music">
              <h2 className={classes.subSection}>
                {translate(
                  <>Music Strategy Comparison: Cafes vs. Gyms</>,
                  <>
                    Comparación de estrategias musicales: cafeterías frente a
                    gimnasios
                  </>,
                  <>
                    Comparaison des stratégies musicales&nbsp;: cafés et
                    gymnases
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비교:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      대.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <section id="gyms-vs-cafes-primary-goal">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        1. Primary Goal
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        1. Objetivo principal
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        1. Objectif principal
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        1.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        주요
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Cafes:&nbsp;</span>{' '}
                        Comfort and longer stays
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés:&nbsp;</span>{' '}
                        Comodidad y estancias más largas
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés&nbsp;:</span>{' '}
                        Confort et séjours plus longs
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          편안함과
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장기
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체류
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Gyms:&nbsp;</span>{' '}
                        Energy and performance enhancement
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Gimnasios:&nbsp;
                        </span>
                        Mejora de la energía y el rendimiento
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Salles de sport&nbsp;:
                        </span>{' '}
                        Amélioration de l’énergie et des performances
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체육관:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          에너지
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          및
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          성능
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          향상
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="gyms-vs-cafes-tempo-range">
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        2. Recommended Tempo Range
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        2. Rango de tempo recomendado
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        2. Plage de tempo recommandée
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        2.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        권장
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        범위
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Cafes:&nbsp;</span>{' '}
                        70–95 BPM (adjust based on time of day)
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés:&nbsp;</span>{' '}
                        70–95 BPM (ajustar según la hora del día)
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés&nbsp;:</span>{' '}
                        70-95 BPM (ajuster en fonction de l'heure de la journée)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          70~95회(시간에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          따라
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          조정)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Gyms:&nbsp;</span>{' '}
                        120–150 BPM (ideal for cardio and high-intensity
                        sessions)
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Gimnasios:&nbsp;
                        </span>{' '}
                        120–150 BPM (ideal para sesiones cardiovasculares y de
                        alta intensidad)
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Salles de sport&nbsp;:
                        </span>{' '}
                        120-150 BPM (idéal pour les séances de cardio et de
                        haute intensité) &nbsp;
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체육관:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          심장
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          박동수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          120-150회(심혈관
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          및
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고강도
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          운동에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이상적)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="gyms-vs-cafes-volume-level">
                <h3 className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        3. Volume Level
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        3. Nivel de volumen
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>3. Volume</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        볼륨
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        레벨
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Cafes:&nbsp;</span>{' '}
                        Moderate to low; supports conversation
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés:&nbsp;</span>{' '}
                        Moderado a bajo; apoya la conversación
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés&nbsp;:</span>{' '}
                        Modéré à faible&nbsp;; prend en charge la conversation
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          보통에서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          낮음;
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          대화를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지원합니다
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Gyms:&nbsp;</span>{' '}
                        Moderate to high; boosts motivation
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Gimnasios:&nbsp;
                        </span>{' '}
                        Moderado a alto; aumenta la motivación
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Salles de sport&nbsp;:
                        </span>{' '}
                        Modéré à élevé&nbsp;; stimule la motivation &nbsp;
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체육관:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          보통에서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          높음;
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          동기
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          부여를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          향상
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="gyms-vs-cafes-music-genres">
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>4. Genre Focus</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        4. Enfoque de género
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>4. Genre ciblé</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        4.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장르
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        집중
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Cafes:&nbsp;</span>{' '}
                        Indie, jazz, acoustic, folk
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés:&nbsp;</span>{' '}
                        Indie, jazz, acústico, folk
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés&nbsp;:</span>{' '}
                        Indie, jazz, acoustique, folk
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          인디,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          재즈,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          어쿠스틱,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          포크
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Gyms:&nbsp;</span>{' '}
                        EDM, hip-hop, rock, pop
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Gimnasios:&nbsp;
                        </span>
                        EDM, hip-hop, rock, pop
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Salles de sport&nbsp;:
                        </span>{' '}
                        hip-hop, rock, pop &nbsp;
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체육관:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          EDM,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          힙합,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          록,
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          팝
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="gyms-vs-cafes-consistency">
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>5. Consistency</span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        5. Consistencia
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>5. Cohérence</span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        5.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        일관성
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Cafes:&nbsp;</span>{' '}
                        Calm, steady, predictable mood
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés:&nbsp;</span>{' '}
                        Estado de ánimo tranquilo, estable y predecible
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés&nbsp;:</span>{' '}
                        Humeur calme, stable et prévisible
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          조용하고
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          꾸준하며
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          예측
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          가능한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분위기
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Gyms:&nbsp;</span>{' '}
                        Energetic and intensity-driven
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Gimnasios:&nbsp;
                        </span>{' '}
                        Energético y basado en la intensidad.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Salles de sport&nbsp;:
                        </span>{' '}
                        Énergique et axé sur l’intensité &nbsp;
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체육관:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          활력이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          넘치고
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          강도에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          중점을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          둡니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="gyms-vs-cafes-customer-goals">
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        6. Customer Behavior Goal
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        6. Objetivo de comportamiento del cliente
                      </span>
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        6. Objectif de comportement du client
                      </span>
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        6.
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        행동
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Cafes:&nbsp;</span>{' '}
                        Encourage customers to stay longer and order more
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés:&nbsp;</span>{' '}
                        Anime a los clientes a quedarse más tiempo y pedir más
                      </>,
                      <>
                        <span className={classes.spanBolder}>Cafés&nbsp;:</span>{' '}
                        Encouragez les clients à rester plus longtemps et à
                        commander davantage
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          카페:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고객이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          더
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          오래
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          머물고
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          더
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          많이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          주문하도록
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장려합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        <span className={classes.spanBolder}>Gyms:&nbsp;</span>{' '}
                        Help users push harder and return regularly
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Gimnasios:&nbsp;
                        </span>{' '}
                        Ayudar a las usuarios a empujar más fuerte y regresar
                        regularmente.
                      </>,
                      <>
                        <span className={classes.spanBolder}>
                          Salles de sport&nbsp;:
                        </span>{' '}
                        Aidez les utilisateurs à faire plus d'efforts et à
                        revenir régulièrement &nbsp;
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체육관:
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고객이
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          정기적으로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          돌아올
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          수
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있도록
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          피트니스에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          성공하도록
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          동기를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          부여하십시오.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <p className={classes.pParagraph + ' ' + classes.textItalic}>
                {translate(
                  <>
                    Even with these differences, both apply the same strategic
                    framework, only that they differ in the application
                    depending on the business goals.
                  </>,
                  <>
                    Aún con estas diferencias, ambos aplican el mismo marco
                    estratégico, sólo que difieren en la aplicación dependiendo
                    de los objetivos de negocio.
                  </>,
                  <>
                    Même avec ces différences, les deux appliquent le même cadre
                    stratégique, sauf qu'ils diffèrent dans l'application en
                    fonction des objectifs commerciaux.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이러한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      차이점에도
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불구하고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      둘
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동일한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      프레임워크를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적용하며
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      목표에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따라
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적용
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      방식이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다릅니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <section id="implementation">
              <h2 className={classes.subSection}>
                {translate(
                  <>Implementation: Building Your Business Playlist</>,
                  <>
                    Implementación: creación de su lista de reproducción
                    empresarial
                  </>,
                  <>
                    Mise en œuvre&nbsp;: créer votre liste de lecture
                    commerciale
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구현:
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비즈니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      재생
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      목록
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      구축
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>Whether you run a cafe or a gym, follow these steps:</>,
                  <>
                    Ya sea que dirija una cafetería o un gimnasio, siga estos
                    pasos:
                  </>,
                  <>
                    Que vous dirigiez un café ou une salle de sport, suivez ces
                    étapes&nbsp;:
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      운영하든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      운영하든
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      단계를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따르세요.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <section id="step-1-define-branded-sound">
                <h3 className={classes.subSection}>
                  {translate(
                    <>Step 1: Define Your Brand Sound</>,
                    <>Paso 1: define el sonido de tu marca</>,
                    <>Étape 1&nbsp;: définissez le son de votre marque</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        1단계:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        브랜드
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        사운드
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        정의
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Create{' '}
                      <span className={classes.spanBolder}>
                        music mood boards
                      </span>{' '}
                      that capture your brand personality:
                    </>,
                    <>
                      Crea{' '}
                      <span className={classes.spanBolder}>
                        paneles de humor musicales
                      </span>{' '}
                      que capturen la personalidad de tu marca:
                    </>,
                    <>
                      Créez des{' '}
                      <span className={classes.spanBolder}>
                        listes d'ambiance musicale
                      </span>{' '}
                      qui capturent la personnalité de votre marque&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        브랜드의
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        개성을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        담은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        컬렉션을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        만들어보세요
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>List 10 adjectives describing your dream atmosphere</>,
                      <>
                        Enumera 10 adjetivos que describan la atmósfera de tus
                        sueños
                      </>,
                      <>
                        Liste 10 adjectifs décrivant l’atmosphère de vos rêves
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          당신의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          꿈의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          분위기를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          묘사하는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          10가지
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          형용사를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          나열해보세요
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Identify 5–10 artists that embody those qualities</>,
                      <>
                        Identifique de 5 a 10 artistas que encarnen esas
                        cualidades
                      </>,
                      <>
                        Identifiez 5 à 10 artistes qui incarnent ces qualités
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          이러한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          자질을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          구현하는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          아티스트
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          5~10명을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          찾아보세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Note tempo ranges that match your goals</>,
                      <>
                        Tenga en cuenta los rangos de tempo que coincidan con
                        sus objetivos
                      </>,
                      <>
                        Notez les plages de tempo qui correspondent à vos
                        objectifs
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          목표에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          맞는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          범위를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          확인하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="step-2-map-dayparts">
                <h3 className={classes.subSection}>
                  {translate(
                    <>Step 2: Map Your Dayparts</>,
                    <>Paso 2: mapea tus franjas horarias</>,
                    <>Étape 2&nbsp;: Cartographiez vos tranches horaires</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        2단계:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시간대
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        매핑
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>Different times require different sonic approaches:</>,
                    <>
                      Diferentes tiempos requieren diferentes enfoques sonoros:
                    </>,
                    <>
                      Différentes époques nécessitent différentes approches
                      sonores&nbsp;:
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        시대에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        따라
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다른
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음향
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        접근
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        방식이
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        필요합니다.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Identify your peak hours and customer needs</>,
                      <>
                        Identifique sus horas pico y las necesidades de sus
                        clientes
                      </>,
                      <>
                        Identifiez vos heures de pointe et les besoins de vos
                        clients
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          피크
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시간대와
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고객
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          요구
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사항을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          파악하세요.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Assign tempo and genre guidelines to each period</>,
                      <>Asignar pautas de tempo y género a cada período</>,
                      <>
                        Attribuez des directives de tempo et de genre à chaque
                        période
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          각
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          기간에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          및
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          장르
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지침을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지정합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Create 3–5 hour blocks with distinct sound profiles</>,
                      <>
                        Cree bloques de 3 a 5 horas con distintos perfiles de
                        sonido
                      </>,
                      <>
                        Créez des blocs de 3 à 5 heures avec des profils sonores
                        distincts
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          뚜렷한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          사운드
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          프로필로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          3~5시간
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          블록
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          만들기
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="step-3-curate-strategic-playlists">
                <h3 className={classes.subSection}>
                  {translate(
                    <>Step 3: Curate Strategic Playlists</>,
                    <>Paso 3: seleccione listas de reproducción estratégicas</>,
                    <>
                      Étape 3&nbsp;: Organiser des listes de lecture
                      stratégiques
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        3단계:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        전략적
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재생목록
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        선별
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>
                      Build{' '}
                      <span className={classes.spanBolder}>
                        curated playlists for businesses
                      </span>{' '}
                      that
                    </>,
                    <>
                      Cree{' '}
                      <span className={classes.spanBolder}>
                        listas de reproducción seleccionadas para empresas
                      </span>{' '}
                      que
                    </>,
                    <></>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        다음과
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        같은
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        비즈니스를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        위해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        선별된
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        재생목록을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        구축하세요.
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Include 50–100 tracks per daypart (prevents repetition)
                      </>,
                      <>
                        Incluye entre 50 y 100 pistas por franja horaria (evita
                        la repetición)
                      </>,
                      <>
                        Incluez 50 à 100 pistes par tranche horaire (évite les
                        répétitions)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시간대당
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          50~100개
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          트랙(반복
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          방지)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Maintain consistent energy within each period</>,
                      <>
                        Mantener una energía constante dentro de cada período
                      </>,
                      <>
                        Maintenir une énergie constante au cours de chaque
                        période
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          각
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          기간
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          내에서
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          일관된
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          에너지를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          유지하십시오
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Transition smoothly between tracks</>,
                      <>Transición fluida entre canciones</>,
                      <>Transition fluide entre les pistes</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          트랙
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          간
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          원활한
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          전환
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>
                        Exclude songs with controversial lyrics or jarring
                        elements
                      </>,
                      <>
                        Excluir canciones con letras controvertidas o elementos
                        discordantes
                      </>,
                      <>
                        Excluez les chansons avec des paroles controversées ou
                        des éléments discordants
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          논란의
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          여지가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          가사나
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          거슬리는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          요소가
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          있는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          노래
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          제외
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
              <section id="step-4-test-and-refine">
                <h3 className={classes.subSection}>
                  {translate(
                    <>Step 4: Test and Refine</>,
                    <>Paso 4: probar y perfeccionar</>,
                    <>Étape 4&nbsp;: tester et affiner</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        4단계:
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        테스트
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        개선
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </h3>
                <p className={classes.pParagraph}>
                  {translate(
                    <>Monitor customer response:</>,
                    <>Monitorear la respuesta del cliente:</>,
                    <>Surveillez la réponse des clients&nbsp;:</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        고객
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        응답
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        모니터링:
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </p>
                <ul className={classes.standardText + ' ' + classes.ulArticles}>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Track dwell time and spending patterns</>,
                      <>
                        Realice un seguimiento del tiempo de permanencia y los
                        patrones de gasto
                      </>,
                      <>Suivez le temps d'arrêt et les habitudes de dépenses</>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          체류
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          시간
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          및
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지출
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          패턴
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          추적
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Solicit feedback (directly or through observation)</>,
                      <>
                        Solicitar retroalimentación (directamente o mediante
                        observación)
                      </>,
                      <>
                        Solliciter des commentaires (directement ou par
                        observation)
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          피드백
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          요청(직접
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          또는
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          관찰을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          통해)
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Adjust volume and tempo based on crowd dynamics</>,
                      <>
                        Ajusta el volumen y el tempo según la dinámica de la
                        multitud.
                      </>,
                      <>
                        Ajustez le volume et le tempo en fonction de la
                        dynamique de la foule
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          군중
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          역학에
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          따라
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          볼륨과
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          템포를
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          조정합니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                  <li className={classes.liArticle}>
                    {translate(
                      <>Refresh the playlists every month to avoid boredom</>,
                      <>
                        Actualiza las listas de reproducción cada mes para
                        evitar el aburrimiento
                      </>,
                      <>
                        Actualisez les playlists chaque mois pour éviter l'ennui
                      </>,
                      <>
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          지루함을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          피하기
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          위해
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          매달
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          재생
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          목록을
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          새로
                        </span>{' '}
                        <span
                          aria-hidden={true}
                          className={classes.asianTextBlock}
                        >
                          고칩니다.
                        </span>
                      </>,
                      selectedLanguage
                    )}
                  </li>
                </ul>
              </section>
            </section>
            <section id="legal-requirements">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Legal Requirement Must Follow</>,
                  <>El requisito legal debe seguir</>,
                  <>L'exigence légale doit suivre</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      법적
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요구
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사항을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따라야
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      합니다
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Both cafes and gyms face identical challenges when it comes
                    to fitness music licensing requirements. Spotify, Apple
                    Music, and YouTube are meant for personal use. Their
                    application in business areas is illegal, risky, and
                    unprofessional.
                  </>,
                  <>
                    Tanto los cafés como los gimnasios enfrentan desafíos
                    idénticos cuando se trata de requisitos de licencia de
                    música fitness. Spotify, Apple Music y YouTube están
                    destinados a uso personal. Su aplicación en ámbitos
                    empresariales es ilegal, arriesgada y poco profesional.
                  </>,
                  <>
                    Les cafés et les salles de sport sont confrontés à des défis
                    identiques en ce qui concerne les exigences en matière de
                    licences pour la musique de fitness. Spotify, Apple Music et
                    YouTube sont destinés à un usage personnel. Leur application
                    dans les domaines commerciaux est illégale, risquée et non
                    professionnelle.
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      피트니스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      라이선스
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      요구
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사항과
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관련하여
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동일한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      문제에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      직면해
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      있습니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Spotify,
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Apple
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      Music
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      및
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      YouTube는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      개인적인
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      용도로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사용됩니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      사업
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      분야에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      적용하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      불법적이고
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      위험하며
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      비전문적입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    <span className={classes.spanBolder}>
                      What You Actually Need
                    </span>
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Lo que realmente necesitas
                    </span>
                  </>,
                  <>
                    <span className={classes.spanBolder}>
                      Ce dont vous avez réellement besoin
                    </span>
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      실제로
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <p className={classes.pParagraph}>
                {translate(
                  <>Both cafes and gyms require:</>,
                  <>Tanto las cafeterías como los gimnasios requieren:</>,
                  <>Les cafés et les salles de sport exigent&nbsp;:</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      모두
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      다음이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      필요합니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>{' '}
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>Public performance licenses (ASCAP, BMI, SESAC)</>,
                    <>Licencias de actuación pública (ASCAP, BMI, SESAC)</>,
                    <>
                      Licences de représentation publique (ASCAP, BMI, SESAC)
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        공공
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        공연
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이선스(ASCAP,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        BMI,
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        SESAC)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Commercial music streaming services</>,
                    <>Servicios comerciales de transmisión de música</>,
                    <>Services commerciaux de streaming musical</>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상업용
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        음악
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스트리밍
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        서비스
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>Proper coverage for composition and recording rights</>,
                    <>
                      Cobertura adecuada de los derechos de composición y
                      grabación
                    </>,
                    <>
                      Couverture adéquate pour les droits de composition et
                      d’enregistrement
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        작곡
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        및
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        녹음권에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적절한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        보장
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
            </section>
            <section id="the-winning-formula">
              <h2 className={classes.subSection}>
                {translate(
                  <>The Winning Formula Revealed</>,
                  <>La fórmula ganadora revelada</>,
                  <>La formule gagnante révélée</>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      승리
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공식
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공개
                    </span>
                  </>,
                  selectedLanguage
                )}
              </h2>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    Winning cafes and gyms follow the same{' '}
                    <span className={classes.spanBolder}>
                      background music strategy
                    </span>{' '}
                    framework:
                  </>,
                  <>
                    Los cafés y gimnasios ganadores siguen el mismo{' '}
                    <span className={classes.spanBolder}>
                      marco estratégico de música ambiental
                    </span>
                  </>,
                  <>
                    Les cafés et salles de sport gagnants suivent le même cadre
                    <span className={classes.spanBolder}>
                      stratégique de musique de fond
                    </span>
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      우승한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      카페와
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      체육관은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      동일한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      배경
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      음악
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      전략
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      프레임워크를
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      따릅니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
              <ul className={classes.standardText + ' ' + classes.ulArticles}>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Know your goal</span>{' '}
                      (comfort vs. energy)
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Conoce tu objetivo
                      </span>{' '}
                      (comodidad vs. energía)
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Connaissez votre objectif
                      </span>{' '}
                      (confort vs énergie)
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        파악하세요(편안함
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        대
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        에너지)
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Match tempo to objective
                      </span>{' '}
                      (lingering vs. performance)
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Igualar el ritmo al objetivo
                      </span>{' '}
                      (permanencia frente a rendimiento)
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Adaptez le tempo à l'objectif
                      </span>{' '}
                      (persistant vs performance)
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        목표에
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        맞춰
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        템포를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        맞추세요
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Maintain brand consistency
                      </span>{' '}
                      through genre and mood
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Mantener la coherencia de la marca
                      </span>{' '}
                      a través del género y el estado de ánimo
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Maintenir la cohérence de la marque
                      </span>{' '}
                      à travers le genre et l’ambiance
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        장르와
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        분위기를
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        통해
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        브랜드
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        일관성
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>
                        Adapt throughout the day
                      </span>{' '}
                      with smart dayparting
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Adáptese a lo largo del día
                      </span>{' '}
                      con franjas horarias inteligentes
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Adaptez-vous tout au long de la journée grâce à une
                      </span>{' '}
                      répartition horaire intelligente
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        스마트
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        데이파츠로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        하루
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        종일
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적응하세요
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
                <li className={classes.liArticle}>
                  {translate(
                    <>
                      <span className={classes.spanBolder}>Stay legal</span>{' '}
                      with proper commercial licensing
                    </>,
                    <>
                      <span className={classes.spanBolder}>
                        Manténgase legal
                      </span>{' '}
                      con la licencia comercial adecuada
                    </>,
                    <>
                      <span className={classes.spanBolder}>Restez légal</span>{' '}
                      avec une licence commerciale appropriée
                    </>,
                    <>
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        적절한
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        상업
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        라이센스로
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        합법성을
                      </span>{' '}
                      <span
                        aria-hidden={true}
                        className={classes.asianTextBlock}
                      >
                        유지하세요
                      </span>
                    </>,
                    selectedLanguage
                  )}
                </li>
              </ul>
              <p className={classes.pParagraph}>
                {translate(
                  <>
                    The formula isn’t about what you play&nbsp;— it’s about why
                    you play it. The businesses that succeed are those that
                    learn to listen{' '}
                    <span className={classes.spanBolder}>strategically.</span>
                  </>,
                  <>
                    La fórmula no se trata de lo que juegas, sino de por qué lo
                    juegas. Las empresas que triunfan son aquellas que aprenden
                    a escuchar{' '}
                    <span className={classes.spanBolder}>
                      estratégicamente.
                    </span>
                  </>,
                  <>
                    La formule ne concerne pas ce que vous jouez, mais la raison
                    pour laquelle vous y jouez. Les entreprises qui réussissent
                    sont celles qui apprennent à écouter de manière{' '}
                    <span className={classes.spanBolder}>stratégique.</span>
                  </>,
                  <>
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      공식은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당신이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      무엇을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플레이하는지에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      아닙니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그것은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      당신이
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      그것을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      플레이하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      이유에
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      관한
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      것입니다.
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      성공하는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기업은
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      듣는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      법을
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      배우는
                    </span>{' '}
                    <span aria-hidden={true} className={classes.asianTextBlock}>
                      기업입니다.
                    </span>
                  </>,
                  selectedLanguage
                )}
              </p>
            </section>
            <hr className={classes.threeDotDivisorBottomOfArticle} />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <BackToArticle
              selectedLanguage={selectedLanguage}
              navigateRoute={props?.navigateRoute}
            />
            <br />
          </Grid.Col>
          <Grid.Col md={24} pl={20} pr={20}>
            <ReadyToTransform
              selectedLanguage={selectedLanguage}
              linkVariant={true}
            />
          </Grid.Col>
          <Grid.Col md={24}>
            <div>
              <br />
              <br />
              <br />
            </div>
          </Grid.Col>
        </Grid>
      </div>
    </div>
  )
}
